import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FetchError } from '@amzn/handshake-painter-typescript-client/v1';
import { useTheme } from '@amzn/meridian/theme';
import { useComplianceBundles } from '@amzn/product-widgets-compliance';
import { useBundle } from '@amzn/react-arb-tools';
import { rio, rioDark } from '@amzn/rio-design-tokens';
import { ThemeProvider } from '@emotion/react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { matchPath, Outlet, useLocation, useParams, useSearchParams } from 'react-router';
import Column from '@amzn/meridian/column';
import { AlertProvider } from './common/alert';
import { AppError, RedirectingToAmazonPage } from './common/errors';
import { Footer } from './common/footer';
import { Header } from './common/header';
import { BouncingBox } from './common/loaders';
import { queryClient } from './api/queryClient';
import { useAnalyticsContext } from './common/metrics';
import { useAppInit, useContent, useDealsStorefront, useMultiAsinProducts, useProductComplianceHighPriority, useProductDetails, useRegularStorefront, } from './hooks';
import { useOnAppInitSuccess } from './hooks/useOnAppInitSuccess';
import { resolveStorefront } from './pages/storefrontPage/utils';
import { useBagStore } from './store/bagStore';
import { ParzivalGatewayAuthErrorType } from './utils/auth';
import { getExperimentArm, isMultiAsinLandingPageEnabled } from './utils/experiments';
import { getMarketplace, isEUMarketplace } from './utils/marketplace';
import { parseAsins } from './utils/asinListUtils';
import { redirectToAmazonApp } from './utils/redirectUtils';
import { emberModernTokens } from './fonts/emberModernTheme';
import { AppTheme } from './fonts/NestedTheme';
import { getRouteData, routes } from './utils/routes';
export const App = () => {
    var _a, _b, _c;
    // `mode` comes from the root Meridian theme (index.tsx, "auto"). The dark-theme removal is
    // gated below by WEBLAB_PAINTER_DEPRECATE_DARK_MODE once appInit resolves the arm.
    const { mode } = useTheme();
    const location = useLocation();
    const { id, asin } = useParams();
    const [searchParams] = useSearchParams();
    const rumClient = useAnalyticsContext();
    /**
     * DO NOT CALL useAppInit in any other file within this components. If useAppInit fails and is called
     * downstream from here, then it will try to fetch again, causing an infinite loop.
     */
    const appData = useAppInit();
    const { isError, isLoading, error, data, isSuccess } = appData;
    const onAppInitSuccess = useOnAppInitSuccess();
    const { listBag } = useBagStore();
    const bagContents = listBag();
    /**
     * 1. For EXPIRED_AUTH error on init, stay in 2.0
     * 2. On any other error (networking, MISSING_AUTH, etc), redirect to 1.0
     */
    const errorCases = [ParzivalGatewayAuthErrorType.EXPIRED_AUTH, ParzivalGatewayAuthErrorType.MISSING_AUTH];
    const redirectOnError = error instanceof FetchError ? !errorCases.includes(error.cause.errorType) : isError;
    // WEBLAB_PAINTER_RETAIL_REDIRECT_LEVER (T1): bad-CX kill switch — off-ramp the session to Amazon
    // retail instead of rendering Handshake. Falsy by default (C/absent render Handshake). Also used to
    // suppress page data fetches below so an off-ramped session sheds load.
    const isRetailRedirectLever = getExperimentArm(data === null || data === void 0 ? void 0 : data.experiments, 'WEBLAB_PAINTER_RETAIL_REDIRECT_LEVER') === 'T1';
    // TODO: Follow this pattern for all pages
    // Preload the route data
    const { route, bundleId } = getRouteData(location.pathname);
    const pdpData = useProductDetails({ enabled: route.isPDP && isSuccess && !isRetailRedirectLever });
    const creatorData = useContent({ enabled: route.isCSF && isSuccess && !isRetailRedirectLever });
    const marketplaceId = getMarketplace(searchParams);
    const euLaunchArm = getExperimentArm(data === null || data === void 0 ? void 0 : data.experiments, 'WEBLAB_PAINTER_EU_LAUNCH');
    // WEBLAB_PAINTER_DEPRECATE_DARK_MODE: T1 = light-only; C = follow OS (existing behavior).
    const isDarkModeDeprecated = getExperimentArm(data === null || data === void 0 ? void 0 : data.experiments, 'WEBLAB_PAINTER_DEPRECATE_DARK_MODE') === 'T1';
    /**
     * The arm arrives with appInit's `data`, so the error branches below can never read it — the very
     * call that carries the arm is the one that failed, leaving the gate to fall back to the OS theme.
     * Dark theme is deprecated, so pin these surfaces (AppError -> InvalidAuthPage / MissingAuthPage,
     * and the redirect interstitial) to light instead, same as the pre-appInit BouncingBox.
     */
    const isLightOnly = isDarkModeDeprecated || isError;
    const themeMode = isLightOnly ? 'light' : mode;
    const theme = themeMode === 'light' ? rio : rioDark;
    // WEBLAB_PAINTER_FONT_MODERNIZATION: T1 = Ember Modern via Meridian theme tokens; C = undefined, today's theme.
    const fontTokens = getExperimentArm(data === null || data === void 0 ? void 0 : data.experiments, 'WEBLAB_PAINTER_FONT_MODERNIZATION') === 'T1' ? emberModernTokens : undefined;
    // T1 only: html.ember-modern styles non-Meridian text. Set before paint; removed on unmount, so the
    // router's errorElement (rendered in App's place) stays in control.
    useLayoutEffect(() => {
        if (!fontTokens)
            return;
        document.documentElement.classList.add('ember-modern');
        return () => document.documentElement.classList.remove('ember-modern');
    }, [fontTokens]);
    const complianceEnabled = route.isPDP && isSuccess && !isRetailRedirectLever && euLaunchArm === 'T1' && isEUMarketplace(marketplaceId);
    const complianceData = useProductComplianceHighPriority({ asin, enabled: complianceEnabled });
    // Log unknown storefront IDs on mount. Handled here rather than in the hooks since both
    // hooks are always called (React hooks can't be conditional), which would fire the event twice.
    const storefront = resolveStorefront(id);
    useEffect(() => {
        if (route.isSFP && !storefront.isValid) {
            rumClient.recordEvent('UNKNOWN_STOREFRONT_ID', { requestedId: id, url: window.location.href });
        }
    }, [id, route.isSFP, storefront.isValid]);
    const sfpEnabled = route.isSFP && isSuccess && !isRetailRedirectLever;
    const dealsStorefrontData = useDealsStorefront({
        enabled: sfpEnabled && storefront.meta.type === 'DEALS',
        storefront,
    });
    const regularStorefrontData = useRegularStorefront({
        enabled: sfpEnabled && storefront.meta.type !== 'DEALS',
        storefront,
    });
    // Multi-ASIN featured products page: render only on its route with the weblab in T1.
    const multiAsinAcceptedAsins = parseAsins(searchParams.get('asins'));
    const isMalpEnabled = isMultiAsinLandingPageEnabled(route.isMALP, data === null || data === void 0 ? void 0 : data.experiments);
    const multiAsinData = useMultiAsinProducts({
        enabled: isMalpEnabled && isSuccess && !isRetailRedirectLever,
        asins: multiAsinAcceptedAsins,
    });
    /**
     * When the route changes, bundleId updates immediately but useBundle still returns the previous bundle
     * for one render cycle (until its internal effect re-fires). We track the previous bundleId so we can
     * detect this mismatch and show the loading state instead of rendering with stale/wrong translations.
     */
    const [bundle, isBundleLoading] = useBundle(bundleId);
    const [commonBundle, isCommonBundleLoading] = useBundle('pages.app');
    const { bundles: complianceBundles, isLoading: isComplianceBundlesLoading } = useComplianceBundles();
    const [prevBundleId, setPrevBundleId] = useState(bundleId);
    const isBundleTransitioning = prevBundleId !== bundleId;
    useEffect(() => {
        setPrevBundleId(bundleId);
    }, [bundleId]);
    const isPageLoading = isLoading ||
        pdpData.isLoading ||
        complianceData.isLoading ||
        creatorData.isLoading ||
        dealsStorefrontData.isLoading ||
        regularStorefrontData.isLoading ||
        multiAsinData.isLoading ||
        isBundleLoading ||
        isCommonBundleLoading ||
        (isComplianceBundlesLoading && complianceEnabled) ||
        isBundleTransitioning;
    // Determine if the current product is Prime eligible based on the route
    const isPrimeFromPDP = route.isPDP && ((_b = (_a = pdpData === null || pdpData === void 0 ? void 0 : pdpData.data) === null || _a === void 0 ? void 0 : _a.prime) === null || _b === void 0 ? void 0 : _b.isPrime);
    const isPrimeFromBag = (route.isPOP || route.isTYP || route.isPSP) && ((_c = bagContents[0]) === null || _c === void 0 ? void 0 : _c.isPrime);
    const isPrime = isPrimeFromPDP || isPrimeFromBag || false;
    const outletContext = {
        appData,
        pdpData,
        complianceData,
        creatorData,
        dealsStorefrontData,
        regularStorefrontData,
        storefront,
        bundle,
        commonBundle,
        complianceBundles,
        multiAsinData,
        isMalpEnabled,
        multiAsinAcceptedAsins,
    };
    /**
     * Clear the product details queries on location change to ensure that we are fetching the latest data.
     * This is tied to selecting product variations and usePrefetchProductDetails
     */
    useEffect(() => {
        if (!matchPath(routes.productDetailPage, location.pathname)) {
            queryClient.removeQueries({ predicate: (query) => query.queryKey.includes('getProductDetails') });
            queryClient.removeQueries({
                predicate: (query) => query.queryKey.includes('getProductComplianceHighPriority'),
            });
            queryClient.removeQueries({
                predicate: (query) => query.queryKey.includes('getProductComplianceDetail'),
            });
        }
    }, [location.pathname]);
    // Reset scroll on page transitions
    useEffect(() => {
        var _a;
        if (!((_a = location.state) === null || _a === void 0 ? void 0 : _a.preventScrollReset)) {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }, [location.pathname, location.state]);
    // AppInit success case
    useEffect(() => {
        if (isSuccess) {
            // Lever (T1): off-ramp to retail with a distinct event name (keeps it out of the alarmed
            // `% sessions redirected` metric), skipping onAppInitSuccess so it stays out of ACCESS_TO_PAINTER_2.
            if (isRetailRedirectLever) {
                redirectToAmazonApp('retail_redirect_lever', { eventName: 'redirect_to_amazon_app_lever' });
            }
            else {
                onAppInitSuccess(data);
            }
        }
        // We perform this in a useEffect so that the BoucingBox continues to render while this logic is executing.
        // Rollout is via pipeline deployment; rollback is a CR revert or an Apollo emergency rollback.
        if (redirectOnError) {
            redirectToAmazonApp('app_init_error');
        }
    }, [redirectOnError, isSuccess, isRetailRedirectLever]);
    // Split from isPageLoading: the handoff asks the host SMA to open the link and does not
    // navigate this page, so it stays mounted until the host reacts. A bare loader gives the
    // customer no explanation and no way out if the host never does. The retail-redirect lever
    // reaches the same interstitial on success — the off-ramp fired in the effect above, so the
    // normal Handshake tree must not render underneath it.
    if (redirectOnError || isRetailRedirectLever) {
        return (_jsx(AppTheme, { mode: isLightOnly ? 'light' : 'auto', fontTokens: fontTokens, children: _jsx(ThemeProvider, { theme: { mode: themeMode, rio: theme }, children: _jsx(Column, { "data-testid": "app-redirecting", minHeight: "100vh", spacing: "none", spacingInset: "none", backgroundColor: theme.semantic.color.background.primary, children: _jsx(RedirectingToAmazonPage, { reason: redirectOnError ? 'app_init_error' : 'retail_redirect_lever' }) }) }) }));
    }
    if (isPageLoading) {
        return _jsx(BouncingBox, {});
    }
    return (_jsx(AppTheme, { mode: isLightOnly ? 'light' : 'auto', fontTokens: fontTokens, children: _jsxs(ThemeProvider, { theme: { mode: themeMode, rio: theme }, children: [isError && (_jsx(Column, { "data-testid": "app-error", minHeight: "100vh", spacing: "none", spacingInset: "none", backgroundColor: theme.semantic.color.background.primary, children: _jsx(AppError, { error: error }) })), !isError && (_jsxs(Column, { "data-testid": "app", minHeight: "100vh", heights: ['fit', 'fill', 'fit'], spacing: "none", spacingInset: "none", backgroundColor: theme.semantic.color.background.primary, children: [_jsx(Header, { isPrime: isPrime, logosLabels: {
                                amazonLogo: commonBundle.getMessage('amazonLogo'),
                                primeLogo: commonBundle.getMessage('amazonPrimeLogo'),
                            } }), _jsx(AlertProvider, { children: _jsx(Outlet, { context: outletContext }) }, location.pathname), _jsx(Footer, { experiments: data === null || data === void 0 ? void 0 : data.experiments })] }))] }) }));
};
